import {
  Activity,
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronRight,
  CloudSun,
  Copy,
  Database,
  Gauge,
  HardDrive,
  HeartPulse,
  Home,
  HousePlug,
  Laptop,
  Lightbulb,
  Moon,
  Network,
  Radio,
  Router,
  Server,
  ShieldCheck,
  Signal,
  Sparkles,
  Sun,
  Wifi,
  type LucideIcon,
} from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";
import { Badge } from "./components/ui/badge";
import { Button } from "./components/ui/button";
import { cn } from "./lib/utils";

type Theme = "light" | "dark";
type CategoryKey = "wifi" | "servicos" | "monitoramento" | "internet";

type PortalItem = {
  slug: string;
  title: string;
  description: string;
  meta: string;
  status: string;
  icon: LucideIcon;
  facts: Array<{ label: string; value: string }>;
  steps: string[];
};

type PortalCategory = {
  key: CategoryKey;
  eyebrow: string;
  title: string;
  description: string;
  metric: string;
  metricLabel: string;
  icon: LucideIcon;
  accent: string;
  wash: string;
  actionLabel: string;
  pageTitle: string;
  pageDescription: string;
  items: PortalItem[];
};

const categories: PortalCategory[] = [
  {
    key: "wifi",
    eyebrow: "Conectividade local",
    title: "Wi-Fi",
    description: "Encontre a rede certa e veja como se conectar em cada ambiente.",
    metric: "3",
    metricLabel: "redes disponíveis",
    icon: Wifi,
    accent: "var(--blue)",
    wash: "var(--blue-wash)",
    actionLabel: "Ver como conectar",
    pageTitle: "Redes da casa",
    pageDescription:
      "Escolha uma rede para consultar cobertura, finalidade e instruções de acesso. Todos os dados abaixo são demonstrativos.",
    items: [
      {
        slug: "casa-principal",
        title: "Casa Principal",
        description: "Para celulares, notebooks, TVs e uso cotidiano da família.",
        meta: "5 GHz · alta velocidade",
        status: "Disponível",
        icon: Signal,
        facts: [
          { label: "SSID", value: "Confins Casa" },
          { label: "Segurança", value: "WPA3" },
          { label: "Cobertura", value: "Áreas internas" },
        ],
        steps: [
          "Abra os ajustes de Wi-Fi do dispositivo.",
          "Selecione a rede Confins Casa.",
          "Use a credencial demonstrativa exibida no ponto de acesso local.",
        ],
      },
      {
        slug: "casa-iot",
        title: "Casa IoT",
        description: "Rede isolada para automação, eletrodomésticos e sensores.",
        meta: "2.4 GHz · dispositivos",
        status: "Disponível",
        icon: HousePlug,
        facts: [
          { label: "SSID", value: "Confins IoT" },
          { label: "Segurança", value: "WPA2" },
          { label: "Acesso", value: "Somente dispositivos" },
        ],
        steps: [
          "Coloque o dispositivo em modo de pareamento.",
          "Durante a instalação, selecione Confins IoT.",
          "Conclua o cadastro no aplicativo do fabricante.",
        ],
      },
      {
        slug: "visitantes",
        title: "Visitantes",
        description: "Internet temporária sem visibilidade dos recursos internos.",
        meta: "Isolada · acesso por convite",
        status: "Sob demanda",
        icon: Laptop,
        facts: [
          { label: "SSID", value: "Confins Visitantes" },
          { label: "Validade", value: "24 horas" },
          { label: "Acesso", value: "Somente internet" },
        ],
        steps: [
          "Solicite um convite ao responsável pela casa.",
          "Abra o link ou QR code recebido.",
          "Confirme a conexão no dispositivo.",
        ],
      },
    ],
  },
  {
    key: "servicos",
    eyebrow: "Atalhos da casa",
    title: "Serviços",
    description: "Acesse automação, arquivos e ferramentas residenciais.",
    metric: "6",
    metricLabel: "serviços online",
    icon: Server,
    accent: "var(--violet)",
    wash: "var(--violet-wash)",
    actionLabel: "Abrir detalhes",
    pageTitle: "Serviços disponíveis",
    pageDescription:
      "Um catálogo simples dos recursos digitais da casa. Os destinos reais serão conectados em uma próxima etapa.",
    items: [
      {
        slug: "home-assistant",
        title: "Home Assistant",
        description: "Luzes, rotinas, presença e cenas dos ambientes.",
        meta: "Automação residencial",
        status: "Online",
        icon: Home,
        facts: [
          { label: "Disponibilidade", value: "99,99%" },
          { label: "Versão", value: "Mock 2026.9" },
          { label: "Acesso", value: "Rede da casa" },
        ],
        steps: [
          "Entre com sua conta residencial.",
          "Selecione um ambiente.",
          "Controle cenas e dispositivos.",
        ],
      },
      {
        slug: "arquivos",
        title: "Arquivos da família",
        description: "Documentos, fotos e mídia organizados em um só lugar.",
        meta: "Armazenamento privado",
        status: "Online",
        icon: HardDrive,
        facts: [
          { label: "Capacidade", value: "6,2 TB" },
          { label: "Uso", value: "41%" },
          { label: "Sincronização", value: "Ativa" },
        ],
        steps: [
          "Abra o compartilhamento.",
          "Escolha sua pasta pessoal.",
          "Salve ou consulte os arquivos.",
        ],
      },
      {
        slug: "jenkins-home",
        title: "Jenkins Home",
        description: "Automação dos projetos e serviços mantidos na residência.",
        meta: "Entrega contínua",
        status: "Online",
        icon: Sparkles,
        facts: [
          { label: "Agentes", value: "1 conectado" },
          { label: "Última build", value: "Sucesso" },
          { label: "Fila", value: "Vazia" },
        ],
        steps: [
          "Escolha um projeto.",
          "Consulte a última execução.",
          "Abra logs e artefatos quando necessário.",
        ],
      },
      {
        slug: "dns-interno",
        title: "DNS interno",
        description: "Nomes amigáveis para os recursos disponíveis dentro de casa.",
        meta: "Infraestrutura de rede",
        status: "Online",
        icon: Network,
        facts: [
          { label: "Consultas", value: "2,8 mil/dia" },
          { label: "Cache", value: "92%" },
          { label: "Estado", value: "Saudável" },
        ],
        steps: [
          "Conecte-se à rede da casa.",
          "Use o nome amigável do serviço.",
          "O DNS seleciona o endereço correto.",
        ],
      },
      {
        slug: "backups",
        title: "Backups",
        description: "Histórico demonstrativo das cópias de segurança residenciais.",
        meta: "Proteção de dados",
        status: "Online",
        icon: Database,
        facts: [
          { label: "Última cópia", value: "Hoje, 03:10" },
          { label: "Retenção", value: "30 dias" },
          { label: "Falhas", value: "0" },
        ],
        steps: [
          "Escolha um recurso protegido.",
          "Consulte os pontos disponíveis.",
          "Solicite uma restauração quando necessário.",
        ],
      },
      {
        slug: "energia",
        title: "Energia da casa",
        description: "Consumo, estimativas e automações para reduzir desperdícios.",
        meta: "Eficiência residencial",
        status: "Em demonstração",
        icon: Lightbulb,
        facts: [
          { label: "Consumo atual", value: "1,8 kW" },
          { label: "Hoje", value: "13,4 kWh" },
          { label: "Tendência", value: "-6%" },
        ],
        steps: [
          "Consulte o consumo atual.",
          "Compare períodos.",
          "Ative uma rotina de economia.",
        ],
      },
    ],
  },
  {
    key: "monitoramento",
    eyebrow: "Casa observável",
    title: "Monitoramento",
    description: "Veja disponibilidade, alertas e a saúde da infraestrutura.",
    metric: "99,98%",
    metricLabel: "disponibilidade",
    icon: Activity,
    accent: "var(--green)",
    wash: "var(--green-wash)",
    actionLabel: "Ver painel",
    pageTitle: "Tudo funcionando",
    pageDescription:
      "Indicadores demonstrativos para entender rapidamente o que está saudável e onde vale prestar atenção.",
    items: [
      {
        slug: "infraestrutura",
        title: "Infraestrutura",
        description: "Servidores, máquinas virtuais e armazenamento residencial.",
        meta: "8 workloads acompanhados",
        status: "Saudável",
        icon: HeartPulse,
        facts: [
          { label: "CPU média", value: "18%" },
          { label: "Memória", value: "46%" },
          { label: "Alertas", value: "0" },
        ],
        steps: [
          "Observe o resumo geral.",
          "Escolha um recurso.",
          "Consulte métricas e eventos recentes.",
        ],
      },
      {
        slug: "rede-residencial",
        title: "Rede residencial",
        description: "Gateways, segmentos e conectividade entre os ambientes.",
        meta: "4 redes monitoradas",
        status: "Estável",
        icon: Radio,
        facts: [
          { label: "Perda", value: "0%" },
          { label: "Dispositivos", value: "37" },
          { label: "Alertas", value: "0" },
        ],
        steps: [
          "Consulte os segmentos.",
          "Escolha um equipamento.",
          "Veja qualidade e histórico.",
        ],
      },
      {
        slug: "protecao",
        title: "Proteção",
        description: "Backups, verificações e sinais importantes de segurança.",
        meta: "Última verificação há 12 min",
        status: "Protegido",
        icon: ShieldCheck,
        facts: [
          { label: "Backups", value: "Em dia" },
          { label: "Incidentes", value: "0" },
          { label: "Cobertura", value: "94%" },
        ],
        steps: [
          "Confira o estado atual.",
          "Consulte o histórico.",
          "Abra um alerta para ver a recomendação.",
        ],
      },
    ],
  },
  {
    key: "internet",
    eyebrow: "Sempre conectados",
    title: "Internet",
    description: "Acompanhe qualidade, velocidade e continuidade dos links.",
    metric: "8 ms",
    metricLabel: "latência agora",
    icon: Router,
    accent: "var(--orange)",
    wash: "var(--orange-wash)",
    actionLabel: "Ver conexão",
    pageTitle: "Conectividade da casa",
    pageDescription:
      "Uma leitura direta dos links residenciais, da qualidade percebida e da contingência automática.",
    items: [
      {
        slug: "link-principal",
        title: "Link principal",
        description: "Conexão preferencial para trabalho, chamadas e entretenimento.",
        meta: "Fibra · rota preferencial",
        status: "Online",
        icon: Gauge,
        facts: [
          { label: "Download", value: "920 Mbps" },
          { label: "Upload", value: "470 Mbps" },
          { label: "Latência", value: "8 ms" },
        ],
        steps: [
          "Veja a qualidade atual.",
          "Compare as últimas 24 horas.",
          "Execute um teste quando necessário.",
        ],
      },
      {
        slug: "contingencia",
        title: "Link de contingência",
        description: "Assume automaticamente quando a rota principal apresenta falhas.",
        meta: "Failover automático",
        status: "Em espera",
        icon: Router,
        facts: [
          { label: "Download", value: "480 Mbps" },
          { label: "Estado", value: "Pronto" },
          { label: "Último teste", value: "Ontem" },
        ],
        steps: [
          "Consulte a disponibilidade.",
          "Veja o último teste.",
          "Acompanhe uma eventual troca de rota.",
        ],
      },
      {
        slug: "qualidade",
        title: "Qualidade percebida",
        description: "Indicadores consolidados para chamadas, jogos e streaming.",
        meta: "Medição contínua",
        status: "Excelente",
        icon: Activity,
        facts: [
          { label: "Jitter", value: "1,4 ms" },
          { label: "Perda", value: "0%" },
          { label: "Nota", value: "9,7/10" },
        ],
        steps: [
          "Escolha um período.",
          "Compare latência e jitter.",
          "Consulte eventos fora do padrão.",
        ],
      },
    ],
  },
];

function getInitialTheme(): Theme {
  const saved = localStorage.getItem("confins-theme");
  if (saved === "light" || saved === "dark") return saved;
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

function SiteShell({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(getInitialTheme);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem("confins-theme", theme);
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute("content", theme === "dark" ? "#10110f" : "#f6f7f2");
  }, [theme]);

  return (
    <div className="min-h-screen overflow-hidden bg-[var(--surface)] text-[var(--foreground)]">
      <div className="ambient ambient-one" />
      <div className="ambient ambient-two" />
      <header className="relative z-20 mx-auto flex w-full max-w-7xl items-center justify-between px-5 py-6 sm:px-8 lg:px-10">
        <Link
          aria-label="Página inicial confins.casa"
          className="group inline-flex items-center gap-3 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
          to="/"
        >
          <span className="grid size-10 place-items-center rounded-2xl bg-[var(--ink)] text-[var(--surface)] shadow-sm transition-transform group-hover:-rotate-3">
            <Home className="size-5" strokeWidth={2.2} />
          </span>
          <span className="text-[15px] font-extrabold tracking-[-0.03em]">
            confins<span className="text-[var(--muted-foreground)]">.casa</span>
          </span>
        </Link>
        <div className="flex items-center gap-2">
          <Badge className="hidden sm:inline-flex">
            <span className="size-1.5 rounded-full bg-emerald-500 shadow-[0_0_0_3px_rgba(16,185,129,.12)]" />
            Casa online
          </Badge>
          <Button
            aria-label={`Ativar modo ${theme === "light" ? "escuro" : "claro"}`}
            onClick={() => setTheme(theme === "light" ? "dark" : "light")}
            size="icon"
            variant="ghost"
          >
            <AnimatePresence initial={false} mode="wait">
              <motion.span
                animate={{ opacity: 1, rotate: 0, scale: 1 }}
                exit={{ opacity: 0, rotate: 25, scale: 0.75 }}
                initial={{ opacity: 0, rotate: -25, scale: 0.75 }}
                key={theme}
              >
                {theme === "light" ? (
                  <Moon className="size-[18px]" />
                ) : (
                  <Sun className="size-[18px]" />
                )}
              </motion.span>
            </AnimatePresence>
          </Button>
        </div>
      </header>
      {children}
      <footer className="relative z-10 mx-auto flex w-full max-w-7xl flex-col gap-4 px-5 pb-10 pt-4 text-sm text-[var(--muted-foreground)] sm:flex-row sm:items-center sm:justify-between sm:px-8 lg:px-10">
        <p>Feito com cuidado para a casa.</p>
        <div className="flex items-center gap-2">
          <ShieldCheck className="size-4" />
          <span>Dados demonstrativos nesta primeira versão.</span>
        </div>
      </footer>
    </div>
  );
}

function PageTransition({ children }: { children: ReactNode }) {
  const reduceMotion = useReducedMotion();
  return (
    <motion.main
      animate={{ opacity: 1, y: 0 }}
      className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-16 sm:px-8 lg:px-10"
      exit={reduceMotion ? undefined : { opacity: 0, y: -8 }}
      initial={reduceMotion ? false : { opacity: 0, y: 14 }}
      transition={{ duration: 0.36, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.main>
  );
}

function HomePage() {
  const reduceMotion = useReducedMotion();
  return (
    <PageTransition>
      <section className="grid items-end gap-10 pb-12 pt-10 lg:grid-cols-[1fr_auto] lg:pb-16 lg:pt-20">
        <div>
          <div className="mb-5 flex items-center gap-2 text-sm font-semibold text-[var(--muted-foreground)]">
            <CloudSun className="size-4 text-amber-500" />
            <span>Belo Horizonte · 24°</span>
            <span aria-hidden="true" className="text-[var(--line-strong)]">/</span>
            <span>terça, 1 de setembro</span>
          </div>
          <h1 className="max-w-4xl text-balance text-5xl font-black leading-[0.98] tracking-[-0.065em] sm:text-6xl lg:text-[82px]">
            Sua casa, em
            <span className="block text-[var(--muted-foreground)]">um só lugar.</span>
          </h1>
          <p className="mt-7 max-w-2xl text-pretty text-base leading-7 text-[var(--muted-foreground)] sm:text-lg">
            Escolha uma área para se conectar, acessar serviços ou acompanhar como a casa está funcionando.
          </p>
        </div>
        <div className="hidden min-w-52 rounded-[28px] border border-[var(--line)] bg-[var(--surface-glass)] p-5 shadow-[var(--soft-shadow)] backdrop-blur-xl lg:block">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-[0.15em] text-[var(--muted-foreground)]">Agora</span>
            <span className="size-2 rounded-full bg-emerald-500" />
          </div>
          <p className="mt-8 text-4xl font-black tracking-[-0.06em]">18:42</p>
          <p className="mt-1 text-sm text-[var(--muted-foreground)]">Noite tranquila em casa</p>
        </div>
      </section>

      <section aria-label="Áreas da casa" className="grid gap-4 md:grid-cols-2">
        {categories.map((category, index) => {
          const Icon = category.icon;
          return (
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              initial={reduceMotion ? false : { opacity: 0, y: 22 }}
              key={category.key}
              transition={{ delay: index * 0.07, duration: 0.48 }}
              whileHover={reduceMotion ? undefined : { y: -5 }}
            >
              <Link
                className="portal-card group relative flex min-h-64 flex-col overflow-hidden rounded-[32px] border border-[var(--line)] bg-[var(--surface-card)] p-6 shadow-[var(--card-shadow)] outline-none transition hover:border-[var(--line-strong)] hover:shadow-[var(--card-shadow-active)] focus-visible:ring-2 focus-visible:ring-[var(--ring)] sm:p-8"
                to={`/${category.key}`}
              >
                <div
                  className="absolute -right-14 -top-14 size-48 rounded-full opacity-25 blur-3xl transition-opacity group-hover:opacity-40"
                  style={{ backgroundColor: category.accent }}
                />
                <div className="relative flex items-start justify-between">
                  <span className="grid size-12 place-items-center rounded-2xl text-white shadow-sm" style={{ backgroundColor: category.accent }}>
                    <Icon className="size-5" strokeWidth={2.2} />
                  </span>
                  <span className="grid size-10 place-items-center rounded-full border border-[var(--line)] bg-[var(--surface-glass)] transition-transform group-hover:translate-x-1">
                    <ArrowRight className="size-4" />
                  </span>
                </div>
                <div className="relative mt-auto grid gap-8 pt-12 sm:grid-cols-[1fr_auto] sm:items-end">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-[var(--muted-foreground)]">{category.eyebrow}</p>
                    <h2 className="mt-2 text-3xl font-black tracking-[-0.045em]">{category.title}</h2>
                    <p className="mt-3 max-w-sm text-sm leading-6 text-[var(--muted-foreground)] sm:text-base">{category.description}</p>
                  </div>
                  <div className="sm:text-right">
                    <p className="text-3xl font-black tracking-[-0.05em]">{category.metric}</p>
                    <p className="mt-1 text-xs font-semibold text-[var(--muted-foreground)]">{category.metricLabel}</p>
                  </div>
                </div>
              </Link>
            </motion.div>
          );
        })}
      </section>
    </PageTransition>
  );
}

function CategoryPage({ category }: { category: PortalCategory }) {
  const Icon = category.icon;
  const reduceMotion = useReducedMotion();
  return (
    <PageTransition>
      <div className="pt-8 sm:pt-14">
        <Link
          className="inline-flex items-center gap-2 text-sm font-bold text-[var(--muted-foreground)] transition hover:text-[var(--foreground)]"
          to="/"
        >
          <ArrowLeft className="size-4" /> Voltar para o início
        </Link>
        <section
          className="mt-8 overflow-hidden rounded-[36px] border border-[var(--line)] p-7 shadow-[var(--soft-shadow)] sm:p-10 lg:p-12"
          style={{ background: category.wash }}
        >
          <div className="flex flex-col gap-8 sm:flex-row sm:items-end sm:justify-between">
            <div className="max-w-3xl">
              <span className="grid size-14 place-items-center rounded-[20px] text-white shadow-sm" style={{ backgroundColor: category.accent }}>
                <Icon className="size-6" />
              </span>
              <p className="mt-8 text-xs font-bold uppercase tracking-[0.18em] text-[var(--muted-foreground)]">{category.eyebrow}</p>
              <h1 className="mt-3 text-4xl font-black tracking-[-0.055em] sm:text-6xl">{category.pageTitle}</h1>
              <p className="mt-5 max-w-2xl text-base leading-7 text-[var(--muted-foreground)] sm:text-lg">{category.pageDescription}</p>
            </div>
            <div className="rounded-3xl border border-[var(--line)] bg-[var(--surface-glass)] px-6 py-5 backdrop-blur-sm">
              <p className="text-3xl font-black tracking-[-0.05em]">{category.metric}</p>
              <p className="mt-1 text-sm text-[var(--muted-foreground)]">{category.metricLabel}</p>
            </div>
          </div>
        </section>

        <section className="py-10 sm:py-14">
          <div className="mb-6 flex items-end justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-[var(--muted-foreground)]">Escolha um item</p>
              <h2 className="mt-2 text-2xl font-black tracking-[-0.04em] sm:text-3xl">Detalhes e acessos</h2>
            </div>
            <Badge>{category.items.length} opções</Badge>
          </div>
          <div className={cn("grid gap-4", category.items.length > 3 ? "md:grid-cols-2 xl:grid-cols-3" : "md:grid-cols-3")}>
            {category.items.map((item, index) => {
              const ItemIcon = item.icon;
              return (
                <motion.div
                  animate={{ opacity: 1, y: 0 }}
                  initial={reduceMotion ? false : { opacity: 0, y: 16 }}
                  key={item.slug}
                  transition={{ delay: index * 0.055, duration: 0.38 }}
                >
                  <Link
                    className="group flex h-full min-h-64 flex-col rounded-[28px] border border-[var(--line)] bg-[var(--surface-card)] p-6 shadow-[var(--card-shadow)] outline-none transition hover:-translate-y-1 hover:border-[var(--line-strong)] hover:shadow-[var(--card-shadow-active)] focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
                    to={`/${category.key}/${item.slug}`}
                  >
                    <div className="flex items-start justify-between">
                      <span className="grid size-11 place-items-center rounded-2xl" style={{ background: category.wash, color: category.accent }}>
                        <ItemIcon className="size-5" />
                      </span>
                      <Badge><span className="size-1.5 rounded-full bg-emerald-500" />{item.status}</Badge>
                    </div>
                    <p className="mt-8 text-xs font-bold uppercase tracking-[0.13em] text-[var(--muted-foreground)]">{item.meta}</p>
                    <h3 className="mt-2 text-xl font-black tracking-[-0.035em]">{item.title}</h3>
                    <p className="mt-3 text-sm leading-6 text-[var(--muted-foreground)]">{item.description}</p>
                    <div className="mt-auto flex items-center justify-between gap-3 pt-8 text-sm font-bold">
                      <span>{category.actionLabel}</span>
                      <ChevronRight className="size-4 transition-transform group-hover:translate-x-1" />
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </section>
      </div>
    </PageTransition>
  );
}

function ItemPage() {
  const { categoryKey, itemSlug } = useParams();
  const category = categories.find((entry) => entry.key === categoryKey);
  const item = category?.items.find((entry) => entry.slug === itemSlug);
  const [actionDone, setActionDone] = useState(false);

  if (!category || !item) return <Navigate replace to="/" />;

  const Icon = item.icon;
  return (
    <PageTransition>
      <div className="pt-8 sm:pt-14">
        <nav aria-label="Navegação estrutural" className="flex flex-wrap items-center gap-2 text-sm font-bold text-[var(--muted-foreground)]">
          <Link className="transition hover:text-[var(--foreground)]" to="/">Início</Link>
          <ChevronRight className="size-3.5" />
          <Link className="transition hover:text-[var(--foreground)]" to={`/${category.key}`}>{category.title}</Link>
          <ChevronRight className="size-3.5" />
          <span className="text-[var(--foreground)]">{item.title}</span>
        </nav>

        <section className="mt-8 grid gap-6 lg:grid-cols-[1.25fr_0.75fr]">
          <div className="rounded-[36px] border border-[var(--line)] bg-[var(--surface-card)] p-7 shadow-[var(--soft-shadow)] sm:p-10 lg:p-12">
            <div className="flex items-start justify-between gap-4">
              <span className="grid size-14 place-items-center rounded-[20px] text-white" style={{ backgroundColor: category.accent }}>
                <Icon className="size-6" />
              </span>
              <Badge><span className="size-1.5 rounded-full bg-emerald-500" />{item.status}</Badge>
            </div>
            <p className="mt-10 text-xs font-bold uppercase tracking-[0.18em] text-[var(--muted-foreground)]">{category.title}</p>
            <h1 className="mt-3 text-4xl font-black tracking-[-0.055em] sm:text-6xl">{item.title}</h1>
            <p className="mt-5 max-w-2xl text-base leading-7 text-[var(--muted-foreground)] sm:text-lg">{item.description}</p>

            <div className="mt-10 grid gap-3 sm:grid-cols-3">
              {item.facts.map((fact) => (
                <div className="rounded-3xl border border-[var(--line)] bg-[var(--surface-elevated)] p-5" key={fact.label}>
                  <p className="text-xs font-bold uppercase tracking-[0.13em] text-[var(--muted-foreground)]">{fact.label}</p>
                  <p className="mt-3 text-lg font-black tracking-[-0.025em]">{fact.value}</p>
                </div>
              ))}
            </div>
          </div>

          <aside className="rounded-[36px] border border-[var(--line)] p-7 shadow-[var(--card-shadow)] sm:p-8" style={{ background: category.wash }}>
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-[var(--muted-foreground)]">Como usar</p>
            <ol className="mt-6 grid gap-5">
              {item.steps.map((step, index) => (
                <li className="grid grid-cols-[auto_1fr] gap-4 text-sm leading-6" key={step}>
                  <span className="grid size-8 place-items-center rounded-full bg-[var(--surface-glass)] font-black" style={{ color: category.accent }}>{index + 1}</span>
                  <span className="pt-1 text-[var(--muted-foreground)]">{step}</span>
                </li>
              ))}
            </ol>
            <Button className="mt-8 w-full" onClick={() => setActionDone(true)}>
              {category.key === "wifi" ? <Copy className="size-4" /> : <ArrowRight className="size-4" />}
              {category.key === "wifi" ? "Preparar conexão" : "Abrir acesso"}
            </Button>
            <AnimatePresence>
              {actionDone && (
                <motion.div
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-4 rounded-2xl border border-[var(--line)] bg-[var(--surface-glass)] p-4 text-sm leading-6 text-[var(--muted-foreground)]"
                  initial={{ opacity: 0, y: 6 }}
                >
                  <div className="flex gap-3">
                    <Check className="mt-0.5 size-4 shrink-0 text-emerald-600" />
                    <p>Fluxo demonstrativo concluído. O endereço ou a credencial real será conectado aqui depois.</p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </aside>
        </section>

        <div className="py-10">
          <Link className="inline-flex items-center gap-2 text-sm font-bold text-[var(--muted-foreground)] transition hover:text-[var(--foreground)]" to={`/${category.key}`}>
            <ArrowLeft className="size-4" /> Voltar para {category.title.toLowerCase()}
          </Link>
        </div>
      </div>
    </PageTransition>
  );
}

function App() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [location.pathname]);

  const categoryRoutes = useMemo(
    () =>
      categories.map((category) => (
        <Route
          element={<CategoryPage category={category} />}
          key={category.key}
          path={`/${category.key}`}
        />
      )),
    [],
  );

  return (
    <SiteShell>
      <AnimatePresence mode="wait">
        <Routes key={location.pathname} location={location}>
          <Route element={<HomePage />} path="/" />
          {categoryRoutes}
          <Route element={<ItemPage />} path="/:categoryKey/:itemSlug" />
          <Route element={<Navigate replace to="/" />} path="*" />
        </Routes>
      </AnimatePresence>
    </SiteShell>
  );
}

export default App;
